// Which uploaded rows the person gate stopped, and whether an approver may force them.
//
// Two places can stop a row. The door refuses to adopt an unowned Account with a reason
// (`outcome: excluded`, `reasons`), and that row never reaches a writer. A row the door did
// pass meets the gate again in `assign_account` (`reason: blocked`, `reasons`, `forceable`),
// which is the only place "owned by <Name>" can arise. Either way the labels are the gate's.

import type { IntakeMode } from './intakeOwner.ts';
import type { IntakeResultRow } from './intakeResults.ts';
import { reassignFromName } from './bankRequest.ts';

// Never forceable, by anyone: mirror of `intake.door.HARD_REQUEST_REASONS`.
// A competitor marked in Salesforce joined them on 7 Oct (GRO-314).
export const HARD_BLOCK_CODES = ['never_contact', 'not_a_company', 'competitor'];

export interface IntakeBlock {
  reasons: string[];
  // True when an admin or leader may assign past every reason.
  forceable: boolean;
}

export function intakeBlock(row: IntakeResultRow): IntakeBlock | null {
  const reasons = (row.reasons || []).map(label => label.trim()).filter(Boolean);

  // The write's answer is the later word, so it wins when there is one.
  if (row.assignStatus === 'refused' && row.assignReason === 'blocked') {
    return { reasons, forceable: row.forceable === true };
  }

  // A strategist's claim refused outright (never-contact) also carries the labels.
  if (row.assignStatus === 'refused' && reasons.length && row.forceable === false) {
    return { reasons, forceable: false };
  }

  // The door: `reason` is the hard code whenever one applies, so it decides forceability.
  if (row.outcome === 'excluded' && reasons.length && !row.assignStatus) {
    return { reasons, forceable: !HARD_BLOCK_CODES.includes(row.reason) };
  }
  return null;
}

// The "Assign anyway" button: an approver, a forceable block, and something to re-send. A
// row left in the bank has no owner to assign to, and one the door never resolved no Account.
export function canForceRow(row: IntakeResultRow, mode: IntakeMode): boolean {
  if (mode !== 'assign') return false;
  const block = intakeBlock(row);
  if (!block?.forceable) return false;
  return Boolean(row.account_id) && Boolean(row.targetOwner);
}

// The confirm repeats every reason, and whom a reassignment takes the account from.
export function forceConfirmLine(row: IntakeResultRow, targetLabel: string): string {
  const reasons = intakeBlock(row)?.reasons || [];
  const listed = reasons.length ? ` ${reasons.join('; ')}.` : '';
  const from = reassignFromName(reasons);
  const reassign = from ? ` This reassigns from ${from}.` : '';
  return `Assign to ${targetLabel} anyway?${listed}${reassign}`;
}
